import { TeamDirectoryBox } from '@lemontaps/lib/types/api';
import {
  InputAdornment,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
} from '@mui/material';
import { Box } from '@mui/system';
import Fuse from 'fuse.js';
import { useState, useMemo, CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import LtDialog from '@/components/LtDialog';
import DebouncedTextField from '@/components/DebouncedTextField';
import { FixedSizeList } from 'react-window';

const LIST_HEIGHT = 350;
const ELEMENT_HEIGHT = 56;

export default function TeamMemberListPopup({
  open,
  onClose,
  title,
  teamMembers,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  teamMembers: TeamDirectoryBox['team'];
}) {
  const [search, setSearch] = useState('');
  const { t } = useTranslation();

  const fuse = useMemo(
    () =>
      new Fuse(teamMembers, {
        keys: ['fullName', 'subtitle'],
        threshold: 0.5,
      }),
    [teamMembers],
  );

  const filteredTeamMembers = useMemo(() => {
    if (!search) return teamMembers;
    return fuse.search(search).map(result => result.item);
  }, [search, teamMembers, fuse]);

  return (
    <>
      <LtDialog title={title} onClose={onClose} open={open} onDone={onClose} withActionDivider>
        <Box>
          <DebouncedTextField
            delay={500}
            value={search}
            onChange={val => setSearch(val as string)}
            fullWidth
            placeholder={t('search')}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FixedSizeList
            height={LIST_HEIGHT}
            width='100%'
            itemCount={filteredTeamMembers.length}
            itemSize={ELEMENT_HEIGHT}
          >
            {getListElement(filteredTeamMembers)}
          </FixedSizeList>
        </Box>
      </LtDialog>
    </>
  );
}

const getListElement = (teamMembers: TeamDirectoryBox['team']) => {
  const Elem = ({ index, style }: { index: number; style: CSSProperties }) => (
    <ListItem
      disablePadding
      key={teamMembers[index].key}
      style={style}
      sx={{ height: ELEMENT_HEIGHT }}
    >
      <ListItemButton
        sx={{ px: 1 }}
        LinkComponent='a'
        href={teamMembers[index].profileUrl || '#'}
        target='_blank'
      >
        <ListItemAvatar>
          <Avatar src={teamMembers[index].imageUrl}></Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={teamMembers[index].fullName}
          secondary={teamMembers[index].subtitle}
        />
      </ListItemButton>
    </ListItem>
  );
  return Elem;
};
